// The store-review test account — ONE mobile number that never sees an OTP and
// never has its password checked.
//
// Why it exists: Apple rejected the build as untestable. Login itself has no OTP
// (see loginFlow.js) — but `Users.DeviceName` binds an account to one handset,
// and logging in from a second one demands a WhatsApp code. A review team logs
// in from machines we have never seen, so they always land on that branch, and
// the code goes to a phone they do not hold. Play's testers hit the same wall.
//
// So this number is exempt from BOTH checks:
//
//   1234567890  —  any password is accepted, the device check never runs.
//
// The exemption is a property of the NUMBER, not of the password. That is the
// point: a reviewer who mistypes the PIN, or who was sent a stale one, must
// still get in. And if the row is missing from `Users` the first login creates
// it, so the account cannot be broken by someone deleting it between
// submissions — which is exactly the state the table was found in.
//
// ⚠️ This number is readable in the shipped JS bundle, so treat it as public
// knowledge. Map DEMO hardware to it, never a customer's home.

import { login, updateUser, registerNewUser } from '../api/api';

export const TEST_MOBILE = '1234567890';
export const TEST_PASSWORD = '1234';

// The number may arrive as '+91 12345 67890', 911234567890 or a Number. Only the
// last ten digits decide, so a reviewer copying it with an ISD prefix still hits
// the exemption rather than a normal — and failing — login.
export function normaliseMobile(v) {
  const digits = String(v == null ? '' : v).replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

export function isTestMobile(v) {
  return normaliseMobile(v) === TEST_MOBILE;
}

const rowsOf = (res) => (Array.isArray(res && res.data) ? res.data : []);

async function loginRows(password) {
  try {
    return rowsOf(await login(TEST_MOBILE, password));
  } catch (e) {
    return [];
  }
}

// AppContext re-logs in on every boot with the STORED MobileNumber + Password,
// so the row handed back has to carry the password the server actually holds
// now — not the one the reviewer typed. Getting this wrong signs them out on
// the next launch, which reads as the account being broken.
function withCreds(rows, password) {
  return rows.map((r, i) => (i === 0
    ? { ...r, MobileNumber: TEST_MOBILE, Password: password }
    : r));
}

// Returns the test account's user rows, creating the row if `Users` has none.
// Never rejects a wrong password — there is no password to be wrong about.
// Returns null only if the backend could not be reached or refused the insert.
export async function resolveTestAccount(enteredPassword) {
  const typed = String(enteredPassword == null ? '' : enteredPassword).trim();

  // 1. Whatever was typed. On a normal test login this is the only call made,
  //    and nothing below it ever runs — no writes on the happy path.
  if (typed && typed !== TEST_PASSWORD) {
    const rows = await loginRows(typed);
    if (rows.length) return withCreds(rows, typed);
  }

  // 2. The canonical PIN.
  let rows = await loginRows(TEST_PASSWORD);
  if (rows.length) return withCreds(rows, TEST_PASSWORD);

  // 3. Neither worked, so either the row holds some third password or it is not
  //    there at all — and the backend has no lookup-by-mobile endpoint to tell
  //    the two apart (only GetUserDetailsbyMobileNumberAndPassword exists). So
  //    force the password to the canonical one; a `where` matching no rows is a
  //    no-op, which is what makes this safe to try before the insert.
  await updateUser({ Password: TEST_PASSWORD }, `MobileNumber=${TEST_MOBILE}`)
    .catch(() => null);
  rows = await loginRows(TEST_PASSWORD);
  if (rows.length) return withCreds(rows, TEST_PASSWORD);

  // 4. No row. Create it with an EMPTY DeviceName so nothing is ever bound and
  //    no later login can be pushed down the OTP branch, even if the exemption
  //    below is one day removed.
  await registerNewUser({
    name: 'App', middleName: '', lastName: 'Review',
    password: TEST_PASSWORD,
    email: 'appreview@smartifyindia.co.in',
    mobile: TEST_MOBILE,
    deviceName: '',
  }).catch(() => null);

  rows = await loginRows(TEST_PASSWORD);
  return rows.length ? withCreds(rows, TEST_PASSWORD) : null;
}
